import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Button,
    Textarea,
    Tooltip,
    makeStyles,
    mergeClasses,
} from '@fluentui/react-components';
import { SendRegular, ArrowClockwiseRegular } from '@fluentui/react-icons';
import TitleSelect from '../TitleSelect/TitleSelect';
import ContextTags from '../TitleSelect/ContextTags';
import FieldValuesMenu, { MenuItem } from './FieldValuesMenu';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column-reverse',
        position: 'relative',
        width: '100%',
    },
    textarea: {
        resize: 'none',
        boxSizing: 'border-box',
        paddingTop: '8px',
        paddingBottom: '50px',
        overflow: 'hidden',
        caretColor: '#007738',
    },
    buttonContainer: {
        position: 'absolute',
        bottom: '12px',
        left: '12px',
        right: '12px',
        display: 'flex',
        justifyContent: 'space-between',
    },
    hideScrollbar: {
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
    },
});

interface TextInputProps {
    value: string;
    onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onSend: () => void;
    onRefresh: () => void;
    isAuthed: boolean;
}

const TextInput: React.FC<TextInputProps> = ({
    value,
    onChange,
    onSend,
    onRefresh,
    isAuthed
}) => {
    const classes = useStyles();
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Field values autocomplete state
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuQuery, setMenuQuery] = useState('');
    const [menuTriggerPos, setMenuTriggerPos] = useState(-1);
    const [selectedField, setSelectedField] = useState<string | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [menuItemCount, setMenuItemCount] = useState(0);
    const menuItemsRef = useRef<MenuItem[]>([]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [value]);

    const closeMenu = useCallback(() => {
        setMenuOpen(false);
        setMenuQuery('');
        setMenuTriggerPos(-1);
        setSelectedField(null);
        setActiveIndex(0);
        setMenuItemCount(0);
    }, []);

    const insertText = useCallback((insertion: string) => {
        // Replace from trigger character to current cursor with the insertion text
        const before = value.slice(0, menuTriggerPos);
        const cursorPos = textareaRef.current?.selectionStart ?? value.length;
        const after = value.slice(cursorPos);
        const newValue = before + insertion + after;

        // Synthesize a change event so the parent state updates
        const nativeEvent = new Event('input', { bubbles: true });
        const syntheticEvent = {
            target: { value: newValue },
            currentTarget: { value: newValue },
            nativeEvent,
            preventDefault: () => {},
            stopPropagation: () => {},
            bubbles: true,
            cancelable: false,
            defaultPrevented: false,
            eventPhase: 0,
            isTrusted: false,
            timeStamp: Date.now(),
            type: 'change',
            isDefaultPrevented: () => false,
            isPropagationStopped: () => false,
            persist: () => {},
        } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
        onChange(syntheticEvent);

        closeMenu();

        // Restore focus and cursor position
        setTimeout(() => {
            if (textareaRef.current) {
                const pos = before.length + insertion.length;
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(pos, pos);
            }
        }, 0);
    }, [value, menuTriggerPos, onChange, closeMenu]);

    const handleSelectField = useCallback((field: string, schema: string) => {
        setSelectedField(field);
        setMenuQuery('');
        setActiveIndex(0);
    }, []);

    const handleSelectValue = useCallback((field: string, fieldValue: string, schema: string) => {
        insertText(`${field}: ${fieldValue} `);
    }, [insertText]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (menuOpen) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIndex((prev) => Math.min(prev + 1, menuItemCount - 1));
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex((prev) => Math.max(prev - 1, 0));
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                closeMenu();
                return;
            }
            if (event.key === 'Tab' || (event.key === 'Enter' && !event.shiftKey)) {
                // Let FieldValuesMenu handle selection via activeIndex
                // We need to trigger the active item
                event.preventDefault();
                // Selection is handled by dispatching a click on the active item
                // We use a ref-based approach: store items and select by index
                const activeItem = menuItemsRef.current[activeIndex];
                if (activeItem) {
                    if (activeItem.type === 'field') {
                        handleSelectField(activeItem.field, activeItem.schema);
                    } else {
                        handleSelectValue(activeItem.field, activeItem.value, activeItem.schema);
                    }
                }
                return;
            }
        } else {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                onSend();
            }
        }
    };
 
    const handleInputChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        onChange(event);

        const newValue = event.target.value;
        const cursorPos = event.target.selectionStart ?? newValue.length;

        if (menuOpen) {
            // Check if the trigger character was deleted (cursor moved before trigger pos)
            if (cursorPos <= menuTriggerPos || !newValue[menuTriggerPos] || (newValue[menuTriggerPos] !== '#' && newValue[menuTriggerPos] !== '@')) {
                closeMenu();
            } else {
                // Extract query: text between trigger char + 1 and cursor
                const q = newValue.slice(menuTriggerPos + 1, cursorPos);
                setMenuQuery(q);
                setActiveIndex(0);
            }
        } else {
            // Detect trigger: # or @ typed
            const charBeforeCursor = newValue[cursorPos - 1];
            if (charBeforeCursor === '#' || charBeforeCursor === '@') {
                // Only trigger if it's at the start or preceded by whitespace
                if (cursorPos === 1 || /\s/.test(newValue[cursorPos - 2])) {
                    setMenuOpen(true);
                    setMenuTriggerPos(cursorPos - 1);
                    setMenuQuery('');
                    setSelectedField(null);
                    setActiveIndex(0);
                }
            }
        }

        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    };

    const handleMenuItemCountChange = useCallback((count: number) => {
        setMenuItemCount(count);
    }, []);

    const handleMenuItemsChange = useCallback((items: MenuItem[]) => {
        menuItemsRef.current = items;
    }, []);

    // Keep a ref of menu items in sync so keyboard selection works
    // FieldValuesMenu will call onItemCountChange with the count;
    // we track items via a callback ref pattern from the menu.

    return (
        <div className={classes.container}>
           {menuOpen && isAuthed && (
                <FieldValuesMenu
                    query={menuQuery}
                    selectedField={selectedField}
                    onSelectField={handleSelectField}
                    onSelectValue={handleSelectValue}
                    onClose={closeMenu}
                    activeIndex={activeIndex}
                    onItemCountChange={handleMenuItemCountChange}
                    onItemsChange={handleMenuItemsChange}
                />
            )}
           {isAuthed ?  <Textarea
                ref={textareaRef}
                className={mergeClasses(
                    classes.textarea,
                    classes.hideScrollbar
                )}
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                style={{
                    border: '1px solid #4D4F51',
                    borderRadius: '8px',
                    backgroundColor: '#3E434B40',
                }}
                placeholder="Message Gaming Segmentation Copilot... (Type # to browse fields)"
            /> : 
            <Textarea
                ref={textareaRef}
                className={mergeClasses(
                    classes.textarea,
                    classes.hideScrollbar
                )}
                disabled={true}
                style={{
                    border: '1px solid #4D4F51',
                    borderRadius: '8px',
                    backgroundColor: '#3E434B40',
                }}
                placeholder="You need to be authenticated to send prompts..."
            /> 
            }
            <div className={classes.buttonContainer}>
            {isAuthed && <TitleSelect></TitleSelect> }
             {isAuthed && <ContextTags></ContextTags> }
                <div>
                    {isAuthed && <Tooltip
                        withArrow
                        content="Clear Session"
                        relationship="label"
                    >
                        <Button
                            appearance="transparent"
                            icon={<ArrowClockwiseRegular />}
                            onClick={onRefresh}
                        ></Button>
                    </Tooltip>
                    }
                     {isAuthed && <Button
                        appearance="transparent"
                        icon={<SendRegular />}
                        onClick={onSend}
                    ></Button>
                   }
                </div>
            </div>
        </div>
    );
};

export default TextInput;
